import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Filter, ChevronDown, ChevronUp, Lock, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminLeadsPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  
  // Filters
  const [filterTake, setFilterTake] = useState('all');
  const [filterKnow, setFilterKnow] = useState('all');
  const [filterWant, setFilterWant] = useState('all');
  const [quizVersion, setQuizVersion] = useState('general');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthToken(data.token);
        setIsAuthenticated(true);
        sessionStorage.setItem('adminLeadsToken', data.token);
        sessionStorage.setItem('adminLeadsExpiry', data.expires_at);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'incorrect password');
      }
    } catch (err) {
      setError('login failed, please try again');
    } finally {
      setIsLoggingIn(false);
    }
  };

  useEffect(() => {
    // Check for existing valid token
    const savedToken = sessionStorage.getItem('adminLeadsToken');
    const savedExpiry = sessionStorage.getItem('adminLeadsExpiry');
    
    if (savedToken && savedExpiry) {
      const expiry = new Date(savedExpiry);
      if (expiry > new Date()) {
        setAuthToken(savedToken);
        setIsAuthenticated(true);
      } else {
        // Clear expired token
        sessionStorage.removeItem('adminLeadsToken');
        sessionStorage.removeItem('adminLeadsExpiry');
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchData();
    }
  }, [isAuthenticated, authToken, quizVersion]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        'Authorization': `Bearer ${authToken}`,
      };
      
      const [leadsRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/leverage/leads?quiz_version=${quizVersion}`, { headers }),
        fetch(`${API_URL}/api/leverage/analytics/stats?quiz_version=${quizVersion}`, { headers }),
      ]);
      
      // Check if auth failed (token expired)
      if (leadsRes.status === 401 || analyticsRes.status === 401) {
        setIsAuthenticated(false);
        setAuthToken('');
        sessionStorage.removeItem('adminLeadsToken');
        sessionStorage.removeItem('adminLeadsExpiry');
        setError('session expired, please log in again');
        return;
      }
      
      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData);
      }
      
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filterTake !== 'all' && lead.take !== filterTake) return false;
    if (filterKnow !== 'all' && lead.know !== filterKnow) return false;
    if (filterWant !== 'all' && lead.want !== filterWant) return false;
    return true;
  });

  const downloadCSV = () => {
    const headers = [
      'Date', 'Quiz Version', 'First Name', 'Email', 'Take', 'Career', 'Work', 'More Money', 
      'Time Off', 'Gift', 'Considered', 'Know', 'Want', 'UTM Source', 
      'UTM Medium', 'UTM Campaign', 'UTM Content', 'Klaviyo Error'
    ];
    
    const rows = filteredLeads.map(lead => [
      new Date(lead.created_at).toLocaleString(),
      lead.quiz_version || 'general',
      lead.first_name,
      lead.email,
      lead.take,
      lead.career,
      lead.work,
      lead.more_money,
      lead.time_off,
      lead.gift,
      lead.considered,
      lead.know,
      lead.want,
      lead.utm_source || '',
      lead.utm_medium || '',
      lead.utm_campaign || '',
      lead.utm_content || '',
      lead.klaviyo_error || '',
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${quizVersion === 'practitioners' ? 'practitioner' : 'leverage'}-quiz-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getTakeBadgeColor = (take) => {
    switch (take) {
      case 'closer_look': return 'bg-green-100 text-green-700';
      case 'full_model': return 'bg-blue-100 text-blue-700';
      case 'products_first': return 'bg-yellow-100 text-yellow-700';
      case 'not_now': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatTake = (take) => {
    return take?.replace(/_/g, ' ') || 'N/A';
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-oat flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full"
        >
          <div className="flex items-center justify-center w-14 h-14 bg-lime rounded-full mx-auto mb-6">
            <Lock className="w-7 h-7 text-ink-deep" />
          </div>
          <h1 className="font-anton text-3xl text-ink text-center lowercase mb-2">admin access</h1>
          <p className="text-ink/60 font-hanken text-center mb-6">enter password to view leads</p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                className="w-full px-4 py-3 rounded-xl border border-mushroom-lt font-hanken focus:outline-none focus:border-lime"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {error && (
              <p className="text-red-500 font-hanken text-sm text-center">{error}</p>
            )}
            
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-ink text-oat font-hanken font-bold py-3 rounded-full hover:bg-ink/90 transition-all disabled:opacity-60"
              data-testid="admin-login-btn"
            >
              {isLoggingIn ? 'logging in...' : 'enter'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oat">
      <div className="max-w-7xl mx-auto px-5 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-anton text-4xl text-ink lowercase">leverage quiz leads</h1>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 bg-ink text-oat font-hanken font-bold px-5 py-2.5 rounded-full hover:bg-ink/90 transition-all"
          >
            <Download className="w-4 h-4" /> download csv
          </button>
        </div>
        
        {/* Quiz version tabs */}
        <div className="inline-flex bg-white border border-mushroom-lt rounded-full p-1 mb-8" data-testid="quiz-version-tabs">
          {[['general', 'general quiz'], ['practitioners', 'practitioner quiz']].map(([value, label]) => (
            <button
              key={value}
              onClick={() => setQuizVersion(value)}
              data-testid={`tab-${value}`}
              className={`px-5 py-2 rounded-full font-hanken text-sm font-bold transition-colors ${
                quizVersion === value ? 'bg-ink text-oat' : 'text-ink/60 hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Analytics Stats */}
        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">intro views</p>
              <p className="font-anton text-2xl text-ink">{analytics.intro_view || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">quiz starts</p>
              <p className="font-anton text-2xl text-ink">{analytics.quiz_start || 0}</p>
            </div>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(q => (
              <div key={q} className="bg-white rounded-xl p-4 border border-mushroom-lt">
                <p className="text-ink/60 font-hanken text-xs uppercase mb-1">Q{q}</p>
                <p className="font-anton text-2xl text-ink">{analytics[`question_${q}`] || 0}</p>
              </div>
            ))}
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">email views</p>
              <p className="font-anton text-2xl text-ink">{analytics.email_view || 0}</p>
            </div>
            <div className="bg-lime rounded-xl p-4">
              <p className="text-ink-deep/70 font-hanken text-xs uppercase mb-1">completions</p>
              <p className="font-anton text-2xl text-ink-deep">{analytics.completion || 0}</p>
            </div>
          </div>
        )}
        
        {/* CTA Click Stats */}
        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">{quizVersion === 'practitioners' ? 'Book Call' : 'RUN IT SMS'}</p>
              <p className="font-anton text-2xl text-ink">{quizVersion === 'practitioners' ? (analytics.cta_book_call || 0) : (analytics.cta_runit_sms || 0)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">{quizVersion === 'practitioners' ? 'Practice Page' : 'Run It Page'}</p>
              <p className="font-anton text-2xl text-ink">{analytics.cta_run_it_page || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">Try Products</p>
              <p className="font-anton text-2xl text-ink">{analytics.cta_try_products || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">CONSULT SMS</p>
              <p className="font-anton text-2xl text-ink">{analytics.cta_consult_sms || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-mushroom-lt">
              <p className="text-ink/60 font-hanken text-xs uppercase mb-1">Instagram</p>
              <p className="font-anton text-2xl text-ink">{analytics.cta_instagram || 0}</p>
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-ink/60" />
            <span className="font-hanken text-sm text-ink/60">filter:</span>
          </div>
          
          <select
            value={filterTake}
            onChange={(e) => setFilterTake(e.target.value)}
            className="px-3 py-2 rounded-lg border border-mushroom-lt font-hanken text-sm bg-white focus:outline-none focus:border-lime"
          >
            <option value="all">all takes</option>
            <option value="closer_look">closer look</option>
            <option value="full_model">full model</option>
            <option value="products_first">products first</option>
            <option value="not_now">not now</option>
          </select>
          
          <select
            value={filterKnow}
            onChange={(e) => setFilterKnow(e.target.value)}
            className="px-3 py-2 rounded-lg border border-mushroom-lt font-hanken text-sm bg-white focus:outline-none focus:border-lime"
          >
            <option value="all">all know</option>
            <option value="comp">compensation</option>
            <option value="day_to_day">day to day</option>
            <option value="support">support</option>
            <option value="company">company</option>
            <option value="all_answers">all of above</option>
          </select>
          
          <select
            value={filterWant}
            onChange={(e) => setFilterWant(e.target.value)}
            className="px-3 py-2 rounded-lg border border-mushroom-lt font-hanken text-sm bg-white focus:outline-none focus:border-lime"
          >
            <option value="all">all want</option>
            <option value="family">family</option>
            <option value="breathing_room">breathing room</option>
            <option value="flexibility">flexibility</option>
            <option value="another_way">another way</option>
            <option value="different">different</option>
          </select>
          
          <span className="font-hanken text-sm text-ink/60 ml-auto">
            {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Leads Table */}
        {loading ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="font-hanken text-ink/60">loading...</p>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="font-hanken text-ink/60">no leads yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-mushroom-lt overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-oat/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60">Date</th>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60">Quiz</th>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60">Name</th>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60">Email</th>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60">Take</th>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60">Know</th>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60">Want</th>
                    <th className="px-4 py-3 text-left font-hanken text-xs uppercase text-ink/60"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <React.Fragment key={lead.id}>
                      <tr
                        className="border-t border-mushroom-lt/50 hover:bg-oat/30 cursor-pointer transition-colors"
                        onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                      >
                        <td className="px-4 py-3 font-hanken text-sm text-ink/70">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 font-hanken text-sm text-ink/70" data-testid={`lead-version-${lead.id}`}>
                          {lead.quiz_version || 'general'}
                        </td>
                        <td className="px-4 py-3 font-hanken text-sm text-ink font-medium">
                          {lead.first_name}
                        </td>
                        <td className="px-4 py-3 font-hanken text-sm text-ink">
                          {lead.email}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full font-hanken text-xs font-medium ${getTakeBadgeColor(lead.take)}`}>
                            {formatTake(lead.take)}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-hanken text-sm text-ink/70">
                          {lead.know?.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-3 font-hanken text-sm text-ink/70">
                          {lead.want?.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-3">
                          {expandedLead === lead.id ? (
                            <ChevronUp className="w-4 h-4 text-ink/40" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-ink/40" />
                          )}
                        </td>
                      </tr>
                      
                      {expandedLead === lead.id && (
                        <tr className="border-t border-mushroom-lt/30 bg-oat/20">
                          <td colSpan={8} className="px-4 py-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">Career</p>
                                <p className="font-hanken text-sm text-ink">{lead.career?.replace(/_/g, ' ')}</p>
                              </div>
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">Work</p>
                                <p className="font-hanken text-sm text-ink">{lead.work?.replace(/_/g, ' ')}</p>
                              </div>
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">More Money</p>
                                <p className="font-hanken text-sm text-ink">{lead.more_money?.replace(/_/g, ' ')}</p>
                              </div>
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">Time Off</p>
                                <p className="font-hanken text-sm text-ink">{lead.time_off?.replace(/_/g, ' ')}</p>
                              </div>
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">Gift</p>
                                <p className="font-hanken text-sm text-ink">{lead.gift?.replace(/_/g, ' ')}</p>
                              </div>
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">Considered</p>
                                <p className="font-hanken text-sm text-ink">{lead.considered?.replace(/_/g, ' ')}</p>
                              </div>
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">UTM Source</p>
                                <p className="font-hanken text-sm text-ink">{lead.utm_source || '-'}</p>
                              </div>
                              <div>
                                <p className="font-hanken text-xs text-ink/50 uppercase mb-1">UTM Content</p>
                                <p className="font-hanken text-sm text-ink">{lead.utm_content || '-'}</p>
                              </div>
                              {lead.klaviyo_error && (
                                <div className="col-span-full">
                                  <p className="font-hanken text-xs text-red-500 uppercase mb-1">Klaviyo Error</p>
                                  <p className="font-hanken text-sm text-red-600">{lead.klaviyo_error}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
